import React, { useEffect, useState, useCallback } from "react";
import styles from "./contact_us.module.css";

import ReCAPTCHA from "react-google-recaptcha";
import {
  TextField,
  FormControl,
  Checkbox,
  FormControlLabel,
  Select,
  Typography,
  InputLabel,
  MenuItem,
  Alert
} from "@mui/material";

import { toast } from "react-toastify";


/* ================= STYLES ================= */

const redAsteriskStyle = { color: "red" };

/* ================= COMPONENT ================= */

function ContactForm({ t, i18n }) {
  const baseURL = "https://portal.banknoteconsult.com/api";
  // const baseURL = "http://localhost:8000/api";
  const lang = i18n.language;

  const [questions, setQuestions] = useState([]); // ✅ must be array
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState("");
  const [checked, setChecked] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /* ================= HANDLERS ================= */
  const textFieldStyle = {
    color: "white",
    borderBottom: "1px solid white",
    textAlign: lang === "ar" ? "right" : "left",
  };

  const selectStyles = {
    textAlign: lang === "ar" ? "right" : "left",
    direction: lang === "ar" ? "rtl" : "ltr",
    "& .MuiInput-underline:after": { borderBottomColor: "#DDB96E" },
    "& .MuiInput-underline:before": {
      borderBottomColor: "rgba(255,255,255,0.6)",
    },
    "& .MuiInputBase-input": { color: "white" },
    "& .MuiInputLabel-root": { color: "white" },
    "& .MuiSvgIcon-root": { color: "white" },
    "& .MuiInputLabel-root.Mui-focused": { color: "white" },
  };

  const handleInputChange = (question) => (event) => {
    const value = event.target.value;
    console.log(event.target.value);

    setFormData((prev) => ({
      ...prev,
      [question.name]: {
        question_id: question.id,
        value: value,
      },
    }));
  };

  const handleSendButtonClick = async () => {
    if (!validateForm()) {
      setFormError(
      lang === "ar"
        ? "من فضلك املأ جميع الحقول المطلوبة"
        : "Please fill all required fields"
    );
    } else {
      setIsLoading(true);
      const payload = {
        lang ,
        answers: Object.values(formData).map((item) => ({
          question_id: item.question_id,
          answer: item.value,
        })),
      };

      try {
        setIsLoading(true);
        const response = await fetch(
          `${baseURL}/finance-form`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(payload),
          },
        );

        const result = await response.json();
        setIsLoading(false);
        if (response.ok) {
        setFormData({}); 
        setFormError("");
        toast.success(result.message);
      } else {
        toast.error(result.message || "Error submitting form");
      }

      } catch (error) {
        setIsLoading(false);
         toast.error(error.message)
        console.error("Submit error:", error);
      }
    }
  };

  /* ================= FETCH QUESTIONS ================= */

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await fetch(`${baseURL}/finance-form`,);
        const data = await response.json();
        setQuestions(Array.isArray(data.data) ? data.data : []);
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    };

    fetchQuestions();
  }, []);

  /* ================= VISIBILITY LOGIC ================= */

  const shouldShowQuestion = useCallback(
    (question) => {
      if (!question.parent_id) return true;

      const parent = questions.find((q) => q.id === question.parent_id);
      if (!parent) return false;

      const selectedOptionId = formData[parent.name]?.value;
      if (!selectedOptionId) return false;
      const selectedOption = parent.options?.find(
        (opt) => opt.id === selectedOptionId,
      );
      return selectedOption.value == 1 ? true : false;
    },
    [questions, formData],
  );

  /* ================= VALIDATION ================= */

  const validateForm = () => {
    for (const q of questions) {
      if (q.is_required && shouldShowQuestion(q)) {
        if (!formData[q.name]?.value) return false;
      }

      if (Array.isArray(q.children)) {
        for (const child of q.children) {
          if (
            child.is_required &&
            shouldShowQuestion(child) &&
            !formData[child.name]?.value
          ) {
            return false;
          }
        }
      }
    }
    return true;
  };

  /* ================= FIELD RENDERER ================= */

  const renderField = useCallback(
    (question) => {
      const label = (
        <span>
          {question[`label_${lang}`]}
          {question.is_required === 1 &&( <span style={redAsteriskStyle}> *</span>)}
        </span>
      );

      switch (question.type) {
        case "text":
          return (
            <TextField
              fullWidth
              key={question.id}
              label={label}
              variant="standard"
              value={formData[question.name]?.value || ""}
              onChange={handleInputChange(question)}
              inputProps={{
                style: {
                  ...textFieldStyle,
                  textAlign: lang === "ar" ? "right" : "left",
                  direction: lang === "ar" ? "rtl" : "ltr",
                },
              }}
              InputLabelProps={{
                style: {
                  color: "white",
                },
                sx: {
                  right: lang === "ar" ? 0 : "auto",
                  left: lang === "ar" ? "auto" : 0,
                  transformOrigin: lang === "ar" ? "top right" : "top left",
                  textAlign: lang === "ar" ? "right" : "left",
                },
              }}
              sx={{
                direction: lang === "ar" ? "rtl" : "ltr",
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#DDB96E",
                },
              }}
            />
          );

        case "select":
          return (
            <FormControl
              fullWidth
              key={question.id}
              variant="standard"
              sx={selectStyles}
            >
              <InputLabel
                sx={{
                  right: lang === "ar" ? 0 : "auto",
                  left: lang === "ar" ? "auto" : 0,
                  transformOrigin: lang === "ar" ? "top right" : "top left",
                }}
              >
                {label}
              </InputLabel>
              <Select
                value={formData[question.name]?.value || ""}
                onChange={handleInputChange(question)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "#262729",
                      color: "white",
                      maxHeight: 200,
                      fontFamily: "Cairo Light",
                    },
                  },
                }}
              >
                {Array.isArray(question.options) &&
                  question.options.map((option) => (
                    <MenuItem
                      key={option.id}
                      value={option.id}
                      sx={{
                        direction: lang === "ar" ? "rtl" : "ltr",
                        textAlign: lang === "ar" ? "right" : "left",
                      }}
                    >
                      {option[`label_${lang}`]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          );

        default:
          return null;
      }
    },
    [formData, lang],
  );

  /* ================= JSX ================= */

  return (
    // <did className="w-100 w-md-50">
    <div className={styles.contactForm}>
      <div className={styles.underlinetextfield}>
        {questions.map(
          (q) =>
            shouldShowQuestion(q) && (
              <div key={q.id} style={{ marginBottom: 20 }}>
                {renderField(q)}

                {/* CHILD QUESTIONS  */}
                {Array.isArray(q.children) &&
                  q.children.map(
                    (child) =>
                      shouldShowQuestion(child) && (
                        <div key={child.id} style={{ marginTop: 40 }}>
                          {" "}
                          {renderField(child)}
                        </div>
                      ),
                  )}
              </div>
            ),
        )}

        <ReCAPTCHA
          sitekey="6LfWlv8pAAAAAIXIm5judvIw4fuWoObkZjsLfEbl"
          onChange={(value) => setIsVerified(!!value)}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              sx={{
                color: "white",
                "&.Mui-checked": { color: "green" },
              }}
            />
          }
          label={
            <Typography variant="body1" sx={{ margin: "0 !important" }}>
              {t("terms")}
            </Typography>
          }
        />

        <button
          className={styles.sendButton}
          onClick={handleSendButtonClick}
          disabled={!isVerified || !checked}
        >
          {isLoading ? <div>Loading..</div> : t("send")}
        </button>
        {formError && (
  <Alert
    severity="error"
    sx={{
      mb: 2,
      direction: lang === "ar" ? "rtl" : "ltr",
      textAlign: lang === "ar" ? "right" : "left",
    }}
  >
    {formError}
  </Alert>
)}
      </div>
    </div>
    // </did>
  );
}

export default ContactForm;
